/**
 * THE SITE'S PUBLIC PLACES, GROUPED (Miles, 2026-10-07: "find the subfolders and link them in drop down like a large
 * sophisticated company would to appeal to different audiences and help users navigate the web application much much
 * easier across the web application").
 *
 * THE GSD MENUS (Miles, 2026-10-15): five groups, in this order: product, solutions, ROI, pricing, company. Each is a
 * pill in the new design's header that opens its mega-menu (D-88), and a column in both footers. Every place is a page
 * or a section of the home page (`/#…`) or of /pricing (`/pricing#…`); the /roi and /solutions addresses are the ones
 * src/config/gsdPublicPages.ts lists (tests/landing/siteNavigation.test.ts holds them equal; they are written here, not
 * imported, so that list's titles and descriptions never ride the first download with today's footer). The solutions
 * group is the seven industries (2026-10-30), in their order, from src/config/industries.ts: plain data, a few names.
 *
 * One list, read by the new design's header (its menus and the phone's sheet), the new design's footer and today's
 * footer (src/components/GlobalFooter.tsx), so the three never offer different places. Plain data with no React and no
 * styles: today's footer rides the first download, the new design's header rides the shell's chunk, and both import it.
 *
 * Every page a place names is a route App.tsx declares (tests/landing/siteNavigation.test.ts reads the routes from
 * App.tsx). What each place is about, a line under its name in the header's menus, lives in
 * ./siteNavigationDescriptions.ts, which only the menus' own chunk imports. A place is known by its `id` there and in
 * the glyph maps, never by its address: two places may share one (none does today).
 */
import { INDUSTRIES } from './industries';
// The unit's name (config/employee-unit.json), never a number: the month one Prest0n employee's allocation buys grows
// on the published schedule for the founding cohort, so the menu names the unit, not its pr0.
import { EMPLOYEE_UNIT_NAME } from './employeeUnit.generated';
import { DEMO_ENTRY_WORDS } from '../demo/demoIndustries';

export type SiteNavGroupId = 'product' | 'solutions' | 'roi' | 'pricing' | 'company';

export interface SiteNavItem {
  /** The place's own name in the descriptions and the glyph maps (unique across the site's places). */
  id: string;
  to: string;
  label: string;
  /**
   * `'inside'`: the way inside prest0 (the public demo, exception M1; Justin, 2026-11-02: "see what's inside" goes to
   * the interactive firms, never to the home page's day any more). The menus, the phone's sheet and both footers draw
   * it as the industry chooser's action wherever the build's lever lets an industry in, and as the link `to` (the day,
   * which leads inside too) where none is.
   */
  opens?: 'inside';
}

export interface SiteNavGroup {
  id: SiteNavGroupId;
  label: string;
  items: readonly SiteNavItem[];
}

export const SITE_NAV_GROUPS: readonly SiteNavGroup[] = [
  {
    id: 'product',
    label: 'product',
    items: [
      { id: 'how-it-works', to: '/#how-it-works', label: 'how it works' },
      { id: 'demo', to: '/#demo', label: DEMO_ENTRY_WORDS.open, opens: 'inside' },
      // /integrations asks a signed-out visitor to sign in first (a ProtectedRoute in App.tsx), so the public menu
      // opens the home page's strip of the apps that connect directly (#integrations, since 2026-10-15)
      { id: 'integrations', to: '/#integrations', label: 'integrations' },
      { id: 'control', to: '/#control', label: 'security & control' },
      // "what's new" (2026-10-21): the one place for every new feature, improvement and fix, month by month. It took
      // the ROI group's "changelog / versions", whose page (the ROI report's versions) now opens it too.
      { id: 'whats-new', to: '/changelog', label: "what's new" },
    ],
  },
  {
    id: 'solutions',
    label: 'solutions',
    // the seven industries (2026-10-30), each its own page; the header's menu sets them in two columns
    items: INDUSTRIES.map((i) => ({ id: `solutions-${i.slug}`, to: `/solutions/${i.slug}`, label: i.label })),
  },
  {
    id: 'roi',
    label: 'ROI',
    items: [
      { id: 'roi', to: '/roi', label: 'ROI by industry' },
      { id: 'roi-calculator', to: '/roi/calculator', label: 'ROI calculator' },
      { id: 'roi-methodology', to: '/roi/methodology', label: 'methodology & assumptions' },
      { id: 'roi-data', to: '/roi/data', label: 'open data & downloads' },
    ],
  },
  {
    id: 'pricing',
    label: 'pricing',
    items: [
      { id: 'pricing', to: '/pricing', label: 'plans & price ladder' },
      { id: 'rate-card', to: '/pricing#rate-card', label: 'rate card: cost per task' },
      { id: 'capacity', to: '/pricing#capacity', label: `what one ${EMPLOYEE_UNIT_NAME}'s month buys` },
      { id: 'pricing-faq', to: '/pricing#faq', label: 'pricing FAQ' },
    ],
  },
  {
    id: 'company',
    label: 'company',
    // "founders" (/team#founders) left on 2026-10-19: the team page it opened leads with them, and "team" opens it.
    // "insights" came back on 2026-10-21, its own place right after the team ("more visible on the footer and header
    // sections ... under the company drop down"): it had been a footer extra since 2026-10-15.
    // "referral program" (Malaika, 2026-10-23: "the referral/affiliate page needs to be linked to the top page buttons
    // on the home page as well as the footer in an organized way"): the program's public page, after insights.
    items: [
      { id: 'team', to: '/team', label: 'team' },
      { id: 'insights', to: '/insights', label: 'insights' },
      { id: 'referrals', to: '/referrals', label: 'referral program' },
      { id: 'trust', to: '/trust', label: 'trust & data handling' },
      { id: 'faq', to: '/#faq', label: 'FAQ' },
    ],
  },
];

/**
 * Pages the footers keep in a group's column though the header's menus no longer name them (2026-10-15), so a page
 * that was one press from every page stays one: the menus are Miles's list, the footer is the site's index.
 */
export const SITE_FOOTER_EXTRAS: Readonly<Partial<Record<SiteNavGroupId, readonly SiteNavItem[]>>> = {
  company: [
    { id: 'about', to: '/about', label: 'about' },
    { id: 'mission', to: '/mission', label: 'mission' },
  ],
};

/** Is `to` the page at `pathname`? A link to a section of a page (`/#faq`, `/pricing#faq`) is never "the page". */
export function isSiteNavCurrent(to: string, pathname: string): boolean {
  if (to.includes('#')) return false;
  return pathname === to;
}
